import { EMPLOYMENT_TYPE } from '../../constants.job';
import { INCOME_CHECKLIST_PAY_TYPES, PAY_FREQUENCY } from '../../constants';

import {
  getIncomeItem,
  getMemberRecords,
  getUniqueMemberIds,
} from './incomeTotals.utils';

export function getPayRate(record) {
  const {
    payFrequency,
    hourlyPay,
    weeklyPay,
    biWeeklyPay,
    semiMonthlyPay,
    monthlyPay,
    annualPay,
  } = record;

  switch (payFrequency) {
    case PAY_FREQUENCY.hourly:
      return Number(hourlyPay) || 0;
    case PAY_FREQUENCY.weekly:
      return Number(weeklyPay) || 0;
    case PAY_FREQUENCY.biweekly:
      return Number(biWeeklyPay) || 0;
    case PAY_FREQUENCY.semimonthly:
      return Number(semiMonthlyPay) || 0;
    case PAY_FREQUENCY.monthly:
      return Number(monthlyPay) || 0;
    case PAY_FREQUENCY.annually:
      return Number(annualPay) || 0;
    default:
      return 0;
  }
}

export function getJobTotals(jobs) {
  if (!jobs || jobs?.length === 0) {
    return {
      jobs: [],
      totalIncome: 0,
    };
  }

  // Get unique memberIds
  const memberIds = getUniqueMemberIds(jobs);

  // Process jobs for each member
  const memberJobs = memberIds.map((memberId) => {
    const memberJobRecords = getMemberRecords(jobs, memberId);

    const jobIncomes = [];

    // Process each job record for this member
    memberJobRecords.forEach((record) => {
      const {
        employmentType,
        startDate,
        endDate,
        payFrequency,
        hoursPerWeek,
        // Military specific
        basePayLongevity,
        proficiency: proficiencyPay,
        foreignDuty: seaAndForeignDutyPay,
        subsistence: subsistenceAllowance,
        quarters: quartersAllowance,
        // Self employed
        annualNet,
        // Seasonal
        doNotWorkStartDate: excludeStartDate,
        doNotWorkEndDate: excludeEndDate,
        seasonalWorkWeeks,
        // Additional income
        anticipatedIncrease12Months,
        anticipatedIncreaseRate,
        anticipatedIncreaseWhen,
        overtimePay,
        overtimeHourRate,
        overtimeAvgHours,
        tipsAndGratuities,
        receiveTipsAvgWeek,
        receiveTipsAvgUnreported,
        commissionsOrBonuses,
        commissionsPayFrequency,
        commissionsPayFrequencyWeekly,
        commissionsPayFrequencyMonthly,
        commissionsPayFrequencyAnnually,
      } = record;

      // Base income calculation based on employment type
      if (employmentType === EMPLOYMENT_TYPE.military) {
        const militaryPayRate = [
          Number(basePayLongevity) || 0,
          Number(proficiencyPay) || 0,
          Number(seaAndForeignDutyPay) || 0,
          Number(subsistenceAllowance) || 0,
          Number(quartersAllowance) || 0,
        ].reduce((sum, value) => sum + value, 0);
        jobIncomes.push({
          employmentType,
          ...getIncomeItem({
            memberId,
            payType: INCOME_CHECKLIST_PAY_TYPES.military,
            startDate,
            endDate,
            payFrequency: PAY_FREQUENCY.monthly,
            payRate: militaryPayRate,
          }),
        });
      } else if (employmentType === EMPLOYMENT_TYPE.selfEmployed) {
        jobIncomes.push({
          employmentType,
          ...getIncomeItem({
            memberId,
            payType: INCOME_CHECKLIST_PAY_TYPES.selfEmployed,
            startDate,
            endDate,
            payFrequency: PAY_FREQUENCY.annually,
            payRate: Number(annualNet) || 0,
          }),
        });
      } else {
        // Regular employment (full-time, part-time, seasonal)
        const basePayRate = getPayRate(record);

        // Base pay
        jobIncomes.push(
          getIncomeItem({
            memberId,
            payType: INCOME_CHECKLIST_PAY_TYPES.basePay,
            startDate,
            endDate,
            payFrequency,
            payRate: basePayRate,
            hoursPerWeek: Number(hoursPerWeek) || 0,
            ...(employmentType === EMPLOYMENT_TYPE.seasonal && {
              seasonalWorkWeeks: Number(seasonalWorkWeeks) || 0,
              excludeStartDate,
              excludeEndDate,
            }),
          }),
        );

        // Additional incomes
        if (anticipatedIncrease12Months === 'yes' && anticipatedIncreaseRate) {
          jobIncomes.push(
            getIncomeItem({
              memberId,
              payType: INCOME_CHECKLIST_PAY_TYPES.anticipatedIncrease,
              startDate: anticipatedIncreaseWhen,
              endDate,
              payFrequency,
              payRate: Number(anticipatedIncreaseRate) || 0,
              hoursPerWeek: Number(hoursPerWeek) || 0,
              ...(employmentType === EMPLOYMENT_TYPE.seasonal && {
                seasonalWorkWeeks: Number(seasonalWorkWeeks) || 0,
              }),
            }),
          );
        }

        if (overtimePay === 'yes' && overtimeHourRate) {
          jobIncomes.push(
            getIncomeItem({
              memberId,
              payType: INCOME_CHECKLIST_PAY_TYPES.overtime,
              startDate,
              endDate,
              payFrequency: PAY_FREQUENCY.hourly,
              payRate: Number(overtimeHourRate) || 0,
              hoursPerWeek: Number(overtimeAvgHours) || 0,
              ...(employmentType === EMPLOYMENT_TYPE.seasonal && {
                seasonalWorkWeeks: Number(seasonalWorkWeeks) || 0,
              }),
            }),
          );
        }

        if (tipsAndGratuities === 'yes') {
          const totalTips =
            (Number(receiveTipsAvgWeek) || 0) +
            (Number(receiveTipsAvgUnreported) || 0);

          jobIncomes.push(
            getIncomeItem({
              memberId,
              payType: INCOME_CHECKLIST_PAY_TYPES.tips,
              startDate,
              endDate,
              payFrequency: PAY_FREQUENCY.weekly,
              payRate: totalTips,
              ...(employmentType === EMPLOYMENT_TYPE.seasonal && {
                seasonalWorkWeeks: Number(seasonalWorkWeeks) || 0,
              }),
            }),
          );
        }

        if (commissionsOrBonuses === 'yes' && commissionsPayFrequency) {
          let bonusPayRate = 0;
          if (commissionsPayFrequency === PAY_FREQUENCY.weekly) {
            bonusPayRate = Number(commissionsPayFrequencyWeekly) || 0;
          } else if (commissionsPayFrequency === PAY_FREQUENCY.monthly) {
            bonusPayRate = Number(commissionsPayFrequencyMonthly) || 0;
          } else if (commissionsPayFrequency === PAY_FREQUENCY.annually) {
            bonusPayRate = Number(commissionsPayFrequencyAnnually) || 0;
          }

          jobIncomes.push(
            getIncomeItem({
              memberId,
              payType: INCOME_CHECKLIST_PAY_TYPES.bonuses,
              startDate,
              endDate,
              payFrequency: commissionsPayFrequency,
              payRate: bonusPayRate,
              ...(employmentType === EMPLOYMENT_TYPE.seasonal && {
                seasonalWorkWeeks: Number(seasonalWorkWeeks) || 0,
              }),
            }),
          );
        }
      }
    });

    // Calculate member's total income
    const memberTotalIncome = jobIncomes.reduce(
      (sum, item) => sum + item.incomeTotal,
      0,
    );

    return {
      memberId,
      jobs: jobIncomes,
      totalIncome: memberTotalIncome,
    };
  });

  // Calculate grand total
  const grandTotal = memberJobs.reduce(
    (sum, member) => sum + member.totalIncome,
    0,
  );

  return {
    jobs: memberJobs,
    totalIncome: grandTotal,
  };
}
